import React, {useEffect, useRef, useState} from 'react';
import SectionTitle from '../../components/site/SectionTitle';
import SplitShowcase from '../../components/site/SplitShowcase';
import PatternRecognitionPage from '../tools/PatternRecognitionPage';
import {useWaitlist} from '../../components/site/WaitlistModal';
import {useSiteContent} from './siteContent';
import {useHeroCanvas} from '../../hooks/useHeroCanvas';
import DisabledPocNotice from '../../components/site/DisabledPocNotice';
import {systemConfigService} from '../../services/systemConfigService';
import styles from './SitePages.module.css';

/**
 * POC 形态识别展示页面
 * 嵌入 /tools/pattern 的 PatternRecognitionPage 进行展示
 *
 * @author page
 * @date 2026-07-03
 */
const PatternPocPage: React.FC = () => {
    const {patternPocPage} = useSiteContent();
    const {openWaitlist} = useWaitlist();
    const heroSectionRef = useRef<HTMLElement>(null);
    const heroCanvasRef = useRef<HTMLCanvasElement>(null);
    const [enabled, setEnabled] = useState<boolean | null>(null);

    useEffect(() => {
        loadConfig();
    }, []);

    useHeroCanvas(heroSectionRef, heroCanvasRef);

    const loadConfig = async () => {
        try {
            const response = await systemConfigService.getPocDisplayConfig();
            if ('0' === response.data.code) {
                setEnabled(response.data.data.patternEnabled);
            } else {
                setEnabled(false);
            }
        } catch (error) {
            console.error('加载POC配置失败:', error);
            setEnabled(false);
        }
    };

    if (enabled === null) {
        return null;
    }

    if (!enabled) {
        return <DisabledPocNotice/>;
    }

    return (
        <div className={styles.page}>
            <section ref={heroSectionRef} className={`${styles.section} ${styles.heroSectionWrapper}`}>
                <canvas ref={heroCanvasRef} className={styles.heroSectionCanvas}/>
                <div style={{position: 'relative', zIndex: 1}}>
                    <SectionTitle
                        description={patternPocPage.sectionTitles.demo.description}
                        eyebrow={patternPocPage.sectionTitles.demo.eyebrow}
                        title={patternPocPage.sectionTitles.demo.title}
                    />
                    <div className={`${styles.pocPatternDemo} ${styles.showcaseConstellation}`}
                         data-hover-stop="constellation">
                        <PatternRecognitionPage/>
                    </div>
                </div>
            </section>

            <section className={styles.section}>
                <SectionTitle
                    description={patternPocPage.sectionTitles.stories.description}
                    eyebrow={patternPocPage.sectionTitles.stories.eyebrow}
                    title={patternPocPage.sectionTitles.stories.title}
                />
                <div className={`${styles.storyStack} site-section`}>
                    {patternPocPage.stories.map((story, index) => (
                        <SplitShowcase block={story} key={story.title} reverse={index % 2 === 1}/>
                    ))}
                </div>
            </section>

            <section className={styles.section}>
                <article className={`site-card ${styles.ctaPanel}`}>
                    <div>
                        <span className="site-eyebrow">{patternPocPage.hero.panelLabel}</span>
                        <h3>{patternPocPage.hero.panelTitle}</h3>
                        <p>{patternPocPage.hero.panelDescription}</p>
                    </div>
                    <div className={styles.ctaActions}>
                        <button className={styles.ctaButton} type="button" onClick={openWaitlist}>
                            {patternPocPage.sectionTitles.cta.primaryLabel}
                        </button>
                    </div>
                </article>
            </section>
        </div>
    );
};

export default PatternPocPage;
